import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Heart, HandHeart, Globe, Users, Gift, Shield } from 'lucide-react';

const Compassion = () => {
  const compassionInitiatives = [
    {
      icon: <Gift className="h-8 w-8" />,
      title: "Humanitarian Fund",
      description: "2.5% of all revenue dedicated to supporting disadvantaged communities through food, clothing, and essential supplies."
    },
    {
      icon: <HandHeart className="h-8 w-8" />,
      title: "Volunteer Programs",
      description: "Employee volunteer time and skills-based pro bono work for nonprofit organizations and community causes."
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Accessible Technology",
      description: "Designing products with accessibility features to ensure our technology serves people of all abilities."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Community Partnerships",
      description: "Collaborating with local organizations to understand and address specific community needs effectively."
    }
  ];

  const supportAreas = [
    {
      area: "Food Security",
      description: "Providing regular meals and nutrition support to families in need across Australia and internationally.",
      locations: ["Remote Australian communities", "Disaster-affected regions", "Urban food deserts", "Refugee support centers"]
    },
    {
      area: "Essential Supplies",
      description: "Distributing clothing, hygiene items, and basic necessities to vulnerable populations.",
      locations: ["Homeless shelters", "Women's refuges", "Community centers", "Emergency relief organizations"]
    },
    {
      area: "Education Access",
      description: "Supporting educational initiatives and providing technology resources for learning.",
      locations: ["Underserved schools", "Adult education programs", "Skill development centers", "Digital literacy initiatives"]
    },
    {
      area: "Emergency Relief",
      description: "Rapid response support for communities facing natural disasters and humanitarian crises.",
      locations: ["Flood and fire affected areas", "International disaster zones", "Conflict-affected regions", "Emergency evacuation centers"]
    }
  ];

  const impactMetrics = [
    { number: "$250K+", label: "Donated to Date", description: "Total humanitarian contributions since founding" },
    { number: "15", label: "Partner Organizations", description: "Established partnerships with charitable groups" },
    { number: "5,000+", label: "People Helped", description: "Individuals directly supported through our programs" },
    { number: "100%", label: "Transparency", description: "Open reporting on all humanitarian activities" }
  ];

  const partnerOrganizations = [
    "Australian Red Cross - Disaster relief and emergency support",
    "Foodbank Australia - Food security and nutrition programs",
    "The Salvation Army - Homelessness and family support services",
    "Oxfam Australia - International development and poverty relief",
    "Local community centers - Direct neighborhood support initiatives",
    "Educational nonprofits - Technology access and digital literacy"
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-8">
              <Heart className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Compassion
            </h1>
            <p className="text-xl text-vincotek-blue font-semibold mb-6">
              Giving Back to Communities in Need
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Compassion drives our commitment to social responsibility and community support. We believe that 
              business success should contribute to the wellbeing of those who need it most, both locally and globally. 
              Our humanitarian initiatives reflect our core belief that technology companies have a responsibility 
              to give back meaningfully.
            </p>
            
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Compassion is not just about giving—it's about understanding, connecting, and creating lasting positive change."
              </blockquote>
              <p className="text-gray-600">
                — The heart of Vincotek's social responsibility
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compassion Initiatives */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Our Compassion Initiatives
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple programs that channel our commitment to compassion into concrete support for communities in need.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {compassionInitiatives.map((initiative, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="text-vincotek-blue mb-6 group-hover:scale-110 transition-transform duration-300">
                    {initiative.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {initiative.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {initiative.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Support Areas */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Areas of Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our humanitarian efforts focus on fundamental human needs and crisis response across multiple regions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {supportAreas.map((area, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {area.area}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {area.description}
                  </p>
                  <div>
                    <h4 className="text-sm font-semibold text-vincotek-dark mb-3 uppercase tracking-wide">
                      Support Locations:
                    </h4>
                    <ul className="space-y-2">
                      {area.locations.map((location, locationIndex) => (
                        <li key={locationIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm text-gray-600">{location}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Compassion in Numbers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Measurable impact of our compassionate giving and community support initiatives.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactMetrics.map((metric, index) => (
              <Card key={index} className="text-center bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-vincotek-blue mb-2">
                    {metric.number}
                  </div>
                  <div className="text-lg font-semibold text-vincotek-dark mb-2">
                    {metric.label}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {metric.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Organizations */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Partner Organizations
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                We work with established, trusted organizations to ensure maximum impact and transparent distribution of support.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="flex items-center mb-6">
                <Globe className="h-8 w-8 text-vincotek-yellow mr-4" />
                <h3 className="text-2xl font-semibold">Trusted Partnership Network</h3>
              </div>
              
              <div className="space-y-4">
                {partnerOrganizations.map((partner, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-3 h-3 bg-vincotek-yellow rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-300 leading-relaxed">{partner}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Join Our Compassion Mission
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Whether through using our products (which contributes to our humanitarian fund), 
              partnering with us, or joining our team, you can be part of creating positive change in communities worldwide.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products-services">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Support Through Use
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  Partner With Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compassion;